import { BookingCard } from "@/components/booking/BookingCard";
import { BookingPayment } from "@/components/booking/BookingPayment";
import { BookingPaymentCard } from "@/components/booking/BookingPaymentCard";
import { BookingRatingBanner } from "@/components/booking/BookingRatingBanner";
import { BookingStatusProgress } from "@/components/booking/BookingStatusProgress";
import { getJobCategoryBadge } from "@/components/booking/booking-card-utils";
import { CustomerInfoCard } from "@/components/booking/CustomerInfoCard";
import { VehicleInformation } from "@/components/booking/VehicleInformation";
import { CancelButton } from "@/components/cancel-button";
import { ScreenHeader } from "@/components/common/ScreenHeader";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors, Colors } from "@/constants/theme";
import { useBooking } from "@/hooks/queries/use-bookings-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { isCustomerRole, isDriverRole } from "@/lib/roles";
import { useUser } from "@/stores/use-auth-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React, { useState } from "react";
import {
  Linking,
  RefreshControl,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function BookingDetailsRoute() {
  return (
    <DesktopAppFrame>
      <BookingDetailsScreen />
    </DesktopAppFrame>
  );
}

function BookingDetailsScreen() {
  const { id } = useLocalSearchParams();
  const router = useRouter();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const desktop = useDesktopLayout();
  const user = useUser();
  const [refreshing, setRefreshing] = useState(false);
  const { data: booking, isLoading: loading, refetch } = useBooking(Number(id));

  const onRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const showActiveBooking =
    booking &&
    (booking.status === "accepted" ||
      booking.status === "arrived" ||
      booking.status === "pickup_inspection" ||
      booking.status === "in_progress" ||
      booking.status === "delivered" ||
      booking.status === "delivery_inspection");

  const canContact =
    showActiveBooking &&
    booking?.payment &&
    (booking.payment.status === "authorized" ||
      booking.payment.status === "captured");

  const handleOpenChat = (receiver?: {
    id: number;
    name: string;
    avatar?: string;
  }) => {
    if (!receiver) return;
    router.push({
      pathname: "/bookings/[id]/chat",
      params: {
        id: Number(id),
        receiverId: receiver.id,
        name: receiver.name,
        avatar: receiver.avatar,
        user: JSON.stringify(receiver),
      },
    });
  };

  const handleCall = (phone?: string) => {
    if (phone) {
      Linking.openURL(`tel:${phone}`);
    }
  };

  const showCancelButton =
    isCustomerRole(user?.role) &&
    booking &&
    (booking.status === "pending" ||
      booking.status === "quoted" ||
      booking.status === "cancelled");

  const handleBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(tabs)/bookings");
    }
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["bottom"]}
    >
      <Stack.Screen
        options={{
          title: `Booking #${booking?.id ?? ""}`,
          headerShown: !desktop,
          headerStyle: {
            backgroundColor: theme.background,
          },
          headerTintColor: theme.text,
          headerTitleAlign: "center",
          headerLeft: () => (
            <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
              <Ionicons
                name="chevron-back"
                size={30}
                color={BrandColors.primary}
              />
            </TouchableOpacity>
          ),
        }}
      />
      {desktop && (
        <ScreenHeader
          title={`Booking #${booking?.id ?? ""}`}
          showMenu={false}
          onBack={handleBack}
        />
      )}
      {loading || !booking ? (
        <Loader />
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            desktop && styles.desktopContent,
          ]}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        >
          <BookingStatusProgress booking={booking} />

          <View style={styles.cardSection}>
            <BookingCard
              booking={booking}
              variant="detail"
              backgroundColor={theme.card}
              borderColor={theme.border}
            />

            {booking.status === "scheduled" ? (
              <View
                style={[styles.secondaryActionButton, { marginTop: 12 }]}
                testID="scheduled_job_notice"
              >
                <Ionicons
                  name="calendar"
                  size={20}
                  color={BrandColors.primary}
                />
                <ThemedText
                  style={styles.secondaryActionText}
                  type="defaultSemiBold"
                >
                  {getJobCategoryBadge("scheduled", booking.scheduled_at)
                    ?.description ?? "Scheduled"}
                </ThemedText>
              </View>
            ) : null}

            {booking.status !== "pending" &&
            booking.status !== "quoted" &&
            booking.status !== "scheduled" &&
            booking.status !== "cancelled" &&
            booking.status !== "completed" &&
            booking.driver ? (
              <>
                <TouchableOpacity
                  style={[styles.secondaryActionButton, { marginTop: 12 }]}
                  onPress={() =>
                    router.push(
                      isDriverRole(user?.role)
                        ? `/bookings/${id}/live-driving`
                        : `/bookings/${id}/live-tracking`,
                    )
                  }
                >
                  <Ionicons
                    name="navigate"
                    size={20}
                    color={BrandColors.primary}
                  />
                  <ThemedText
                    style={styles.secondaryActionText}
                    type="defaultSemiBold"
                  >
                    Return to Recovery
                  </ThemedText>
                </TouchableOpacity>
              </>
            ) : null}
          </View>

          <VehicleInformation booking={booking} />

          {isCustomerRole(user?.role) && booking.driver ? (
            <View style={styles.section}>
              <CustomerInfoCard
                name={booking.driver.name}
                roleLabel="Driver"
                subtitle="Driver / Operator"
                avatar={booking.driver.avatar}
                rating={booking.driver.reputation_score}
                loyaltyTier={booking.driver.loyalty_tier}
                isVerified={booking.driver.is_top_rated}
                onChat={() => handleOpenChat(booking.driver)}
                onCall={() => handleCall(booking.driver?.phone)}
                showActions={!!canContact}
              />
            </View>
          ) : null}

          {isDriverRole(user?.role) && booking.customer ? (
            <View style={styles.section}>
              <CustomerInfoCard
                name={booking.customer.name}
                roleLabel="Customer"
                subtitle="Customer"
                avatar={booking.customer.avatar}
                rating={booking.customer.reputation_score}
                loyaltyTier={booking.customer.loyalty_tier}
                isVerified={booking.customer.is_top_rated}
                onChat={() => handleOpenChat(booking.customer)}
                onCall={() => handleCall(booking.customer?.phone)}
                showActions={!!canContact}
              />
            </View>
          ) : null}

          {booking.payment ? (
            <BookingPaymentCard
              booking={booking}
              role={isDriverRole(user?.role) ? "driver" : "customer"}
            />
          ) : null}

          {booking.user_rating ? (
            <BookingRatingBanner rating={booking.user_rating.rating} />
          ) : null}

          <View style={styles.actionsSection}>
            <BookingPayment
              booking={booking}
              user={user}
              onSuccess={() => {
                refetch();
              }}
            />

            {(booking.status === "pending" || booking.status === "quoted") &&
            !booking.driver ? (
              <View style={styles.infoMessage}>
                <Ionicons
                  name="information-circle-outline"
                  size={20}
                  color={BrandColors.primary}
                />
                <ThemedText style={styles.infoMessageText} type="extraSmall">
                  Live tracking will be available once a driver accepts your
                  booking.
                </ThemedText>
              </View>
            ) : null}

            {isDriverRole(user?.role) &&
            booking.status === "accepted" &&
            booking.payment &&
            booking.payment.status !== "authorized" &&
            booking.payment.status !== "captured" ? (
              <View style={styles.infoMessage}>
                <Ionicons
                  name="time-outline"
                  size={20}
                  color={BrandColors.warning}
                />
                <ThemedText
                  style={[
                    styles.infoMessageText,
                    { color: BrandColors.warning },
                  ]}
                  type="extraSmall"
                >
                  Waiting for customer payment...
                </ThemedText>
              </View>
            ) : null}

            {booking.status === "completed" && !booking.user_rating ? (
              <TouchableOpacity
                style={styles.primaryActionButton}
                onPress={() => router.push(`/rate-booking/${id}`)}
              >
                <Ionicons name="star" size={20} color={BrandColors.white} />
                <ThemedText
                  style={styles.primaryActionText}
                  type="defaultSemiBold"
                >
                  Rate{" "}
                  {isDriverRole(user?.role)
                    ? booking.customer?.name
                    : booking.driver?.name}
                </ThemedText>
              </TouchableOpacity>
            ) : null}

            {/* {[
              "accepted",
              "arrived",
              "pickup_inspection",
              "in_progress",
              "delivered",
              "delivery_inspection",
            ].includes(booking.status) ? (
              <TouchableOpacity
                style={[styles.primaryActionButton, styles.panicButton]}
                onPress={() =>
                  router.push({
                    pathname: "/panic",
                    params: { bookingId: id },
                  })
                }
              >
                <Ionicons
                  name="alert-circle"
                  size={20}
                  color={BrandColors.white}
                />
                <ThemedText
                  style={styles.primaryActionText}
                  type="defaultSemiBold"
                >
                  Emergency / Panic Report
                </ThemedText>
              </TouchableOpacity>
            ) : null} */}

            {showCancelButton &&
            booking.status !== "cancelled" &&
            (booking.status === "pending" || booking.status === "quoted") ? (
              <CancelButton
                id={id as string}
                serviceType={booking.service_type}
                style={styles.cancelButtonWrapper}
              />
            ) : null}
          </View>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
    gap: 16,
  },
  desktopContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  cardSection: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  section: {
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  actionsSection: {
    paddingHorizontal: 16,
    paddingTop: 8,
    gap: 12,
  },
  secondaryActionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    backgroundColor: BrandColors.white,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  secondaryActionText: {
    color: BrandColors.primary,
  },
  primaryActionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    backgroundColor: BrandColors.primary,
    borderRadius: 14,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  primaryActionText: {
    color: BrandColors.white,
  },
  panicButton: {
    backgroundColor: BrandColors.error,
  },
  infoMessage: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 12,
    padding: 14,
  },
  infoMessageText: {
    flex: 1,
    color: BrandColors.darkGray,
    lineHeight: 18,
  },
  cancelButtonWrapper: {
    marginBottom: 0,
  },
  cancelledButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: BrandColors.white,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: "#FECACA",
    paddingVertical: 14,
  },
  cancelledButtonText: {
    color: BrandColors.error,
  },
  headerButton: {
    backgroundColor: "transparent",
    padding: 0,
    margin: 0,
  },
});
